// The Briefing for an opened image — the UI-CALCULATED kind.
//
// Nothing is fetched here. Everything on this strip is either already on the item (its title, the
// URL it was opened from) or measured by the browser once the picture has loaded (its pixel size,
// and the byte size when the server bothered to send a Content-Length). That is the whole point of
// the split: an image needs no round-trip to be described, so it does not make one.
//
// The measured half arrives late — the strip renders the moment the image opens and fills in its
// size when the <img> fires `load`. See `briefing/reportImageMeta`.

import { briefing } from './rows'

/**
 * Will the strip's IMAGE briefing describe this previewed item?
 *
 * The same question is asked in two places and must get the same answer in both: the briefing
 * store picks `imageBriefing` with it, and the Context Panel capsule uses it to STAND DOWN — an
 * open picture was being described twice, once in the capsule above the box (title · source ·
 * saved · pack) and again in the strip inside it (title · source · type · size). Two briefings
 * stacked on one subject, most visibly on a Research Pack's Images view. The strip wins: it is
 * the briefing, it carries what the browser measured, and the capsule is for the moments the
 * strip has nothing to say about.
 *
 * Kept here, beside the builder it guards, so the predicate and the briefing can never drift.
 */
export function isImagePreview(item) {
  const it = item?.item || item
  if (!it) return false
  return it.kind === 'photo' || /^image\//i.test(it.contentType || '')
}

/** "example.com" — where the picture came from, which is the honest answer to "source". */
export function imageSource(url) {
  try {
    return new URL(String(url)).hostname.replace(/^www\./, '')
  } catch {
    return ''
  }
}

// A file extension is a poor type but it is usually the ONLY type an <img> URL carries — the
// server's Content-Type is not visible to script for a cross-origin image.
const EXT = /\.([a-z0-9]{2,5})(?:\?|#|$)/i
const TYPE_NAMES = { jpg: 'JPEG', jpeg: 'JPEG', png: 'PNG', gif: 'GIF', webp: 'WebP', avif: 'AVIF', svg: 'SVG', bmp: 'BMP', heic: 'HEIC', tif: 'TIFF', tiff: 'TIFF' }

/** "JPEG" — from the item's declared contentType if it has one, else from the URL's extension. */
export function imageType(item) {
  const declared = String(item?.contentType || '')
  const fromMime = declared.split('/')[1]
  if (declared.startsWith('image/') && fromMime) {
    const key = fromMime.split('+')[0].toLowerCase()
    return TYPE_NAMES[key] || key.toUpperCase()
  }
  const m = EXT.exec(String(item?.url || ''))
  if (!m) return ''
  const key = m[1].toLowerCase()
  return TYPE_NAMES[key] || ''
}

/** "1.4 MB" — bytes in the unit a person reads, or '' when the size is unknown. */
export function formatBytes(bytes) {
  const n = Number(bytes)
  if (!Number.isFinite(n) || n <= 0) return ''
  const units = ['B', 'KB', 'MB', 'GB']
  let value = n
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit += 1 }
  // Whole bytes and KB read better without a decimal; MB and up need one to stay informative.
  const rounded = unit <= 1 ? Math.round(value) : Math.round(value * 10) / 10
  return `${rounded} ${units[unit]}`
}

/** "2400 × 1600" — the picture's real pixel size, once the browser has loaded it. */
export function formatDimensions(meta) {
  const w = Number(meta?.width)
  const h = Number(meta?.height)
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return ''
  return `${w} × ${h}`
}

/**
 * The briefing for the image currently open in the preview.
 *
 * @param item the previewed workboard item
 * @param meta what the UI measured after load: { width, height, bytes }
 * @returns a briefing, or null when nothing is open
 */
export function imageBriefing(item, meta) {
  if (!item) return null

  const dimensions = formatDimensions(meta)
  const bytes = formatBytes(meta?.bytes ?? item?.size)
  // One "size" row rather than two: the pixel size is what people mean by how big a picture is,
  // and the byte size qualifies it. Either alone is still a useful row.
  const size = [dimensions, bytes].filter(Boolean).join(' · ')

  return briefing({
    kind: 'image',
    title: item.title || 'Image',
    titleIcon: 'gallery',
    rows: [
      { key: 'source', icon: 'global', text: imageSource(item.url) },
      { key: 'type', icon: 'slider-vertical', text: imageType(item) },
      { key: 'size', icon: 'gallery', text: size },
    ],
  })
}
